import { useState, useEffect } from "react";
import { Sparkles, X, PenTool, Loader2, Wand2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

const STORAGE_KEY = "jobgrid_pending_job_hint";

/**
 * Persist the vacancy context that the user just navigated away from
 * (typically via the < 40 match-score trust-indicator button).
 */
export function stashPendingJobHint({ jobTitle, jobDescription, matchSummary }) {
  if (!jobTitle) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      jobTitle: String(jobTitle).slice(0, 240),
      jobDescription: String(jobDescription || "").slice(0, 8000),
      matchSummary: String(matchSummary || "").slice(0, 1000),
      at: new Date().toISOString(),
    }));
  } catch { /* localStorage disabled — silently ignore */ }
}

function readPendingJobHint() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Auto-expire hints older than 30 minutes so they don't linger forever.
    if (parsed?.at) {
      const age = Date.now() - new Date(parsed.at).getTime();
      if (Number.isFinite(age) && age > 30 * 60 * 1000) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
    }
    return parsed?.jobTitle ? parsed : null;
  } catch { return null; }
}

/**
 * Subtle "Ontbreekt de juiste variant?" block shown on the CVs overview.
 * Two escape hatches:
 *  - Generate a fresh AI-tailored variant in one click (primary CTA), or
 *  - Open the CV Generator to build one manually (secondary).
 */
export default function PendingJobHint({ onOpenBuilder, onGenerated }) {
  const { t } = useT();
  const [ctx, setCtx] = useState(() => readPendingJobHint());
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setCtx(readPendingJobHint());
  }, []);

  const dismiss = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setCtx(null);
  };

  const generate = async () => {
    if (!ctx || generating) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/resumes/ai/generate-for-job", {
        job_title: ctx.jobTitle,
        job_description: ctx.jobDescription || "",
      });
      toast.success(t("pjhGenerated", { name: data.name }));
      dismiss();
      onGenerated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pjhGenerateFailed"));
    } finally {
      setGenerating(false);
    }
  };

  if (!ctx) return null;

  return (
    <div
      data-testid="pending-job-hint"
      className="border border-amber-600 bg-amber-50 p-4 flex items-start gap-3"
    >
      <Sparkles size={16} strokeWidth={1.5} className="text-amber-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-serif text-base leading-tight mb-1 text-amber-900">
          {t("pjhTitle", { job: ctx.jobTitle })}
        </p>
        {ctx.matchSummary && (
          <p className="text-sm text-amber-900/80 leading-relaxed mb-3">{ctx.matchSummary}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="pending-job-hint-generate"
            type="button"
            onClick={generate}
            disabled={generating}
            className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 transition-colors px-3 py-1.5 inline-flex items-center gap-2 disabled:opacity-60"
          >
            {generating
              ? <><Loader2 size={12} strokeWidth={1.75} className="animate-spin" /> {t("pjhGenerating")}</>
              : <><Wand2 size={12} strokeWidth={1.75} /> {t("pjhGenerate")}</>}
          </button>
          <button
            data-testid="pending-job-hint-open"
            type="button"
            onClick={() => { onOpenBuilder?.(); dismiss(); }}
            disabled={generating}
            className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-amber-800 bg-white text-amber-900 hover:bg-amber-800 hover:text-white transition-colors px-2.5 py-1 inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            <PenTool size={11} strokeWidth={1.75} /> {t("pjhOpenBuilder")}
          </button>
        </div>
      </div>
      <button
        data-testid="pending-job-hint-dismiss"
        type="button"
        onClick={dismiss}
        disabled={generating}
        title={t("pjhDismiss")}
        className="p-1 text-amber-700 hover:text-amber-900 shrink-0 disabled:opacity-40"
      >
        <X size={14} strokeWidth={1.75} />
      </button>
    </div>
  );
}
